/**
 * Tab Manager - Main Orchestrator
 * Coordinates tab management modules
 */

import { TabStateManager } from './tab-state.js';
import { TabHandlers } from './tab-handlers.js';
import { TabNavigation } from './tab-navigation.js';

class TabManager {
    constructor() {
        this.initialized = false;
        this.state = new TabStateManager();
        this.handlers = new TabHandlers(
            this.state, 
            this.triggerTabEvent.bind(this),
            {
                updateUrlHash: this.updateUrlHash.bind(this),
                loadTabContent: this.loadTabContent.bind(this),
                loadContentTabData: this.loadContentTabData.bind(this)
            }
        );
        this.navigation = new TabNavigation(this.state, this.triggerTabEvent.bind(this));
    }
    
    /**
     * Initialize tab events
     */
    async initializeTabEvents() {
        if (this.initialized) {
            return true;
        }
        
        try {
            if (debugMode === '1') {

            }
            
            await this.bindMainTabEvents();
            await this.bindContentTabEvents();
            await this.bindNavigationEvents();
            await this.bindKeyboardEvents();
            
            this.initialized = true;
            
            if (debugMode === '1') {

            }
            return true;
            
        } catch (error) {
            if (debugMode === '1') {
            }
            return false;
        }
    }
    
    /**
     * Bind main tab events
     */
    async bindMainTabEvents() {
        try {
            const mainTabs = document.querySelectorAll('#nav-tab button[data-bs-toggle="tab"]');
            
            mainTabs.forEach((tabEl, index) => {
                const tabId = tabEl.getAttribute('id') || `main-tab-${index}`;
                
                this.state.registerTab(tabId, {
                    element: tabEl,
                    isActive: tabEl.classList.contains('active'),
                    target: tabEl.getAttribute('data-bs-target') || tabEl.getAttribute('href')
                });
                
                if (tabEl.classList.contains('active')) {
                    this.state.setPreviousActiveTabId(tabId);
                }
                
                tabEl.addEventListener('mousedown', (event) => {
                    const wasActive = tabEl.classList.contains('active') || tabEl.getAttribute('aria-selected') === 'true';
                    if (wasActive) {
                        tabEl.setAttribute('data-was-active-before-click', 'true');
                    } else {
                        tabEl.removeAttribute('data-was-active-before-click');
                    }
                }, true);
                
                tabEl.addEventListener('click', (event) => {
                    // Prevent immediate hash update by intercepting click
                    const tabLink = tabEl.getAttribute('tab-link');
                    if (tabLink) {
                        const desiredHash = `#${tabLink}`;
                        const currentHash = window.location.hash;
                        
                        // If hash needs to change, update through debounced mechanism
                        if (currentHash !== desiredHash) {
                            // Prevent default hash update
                            event.preventDefault();
                            
                            // Update hash through debounced mechanism (2 seconds)
                            if (window.filterController && typeof window.filterController.updateUrlWithFilters === 'function') {
                                window.filterController.updateUrlWithFilters(true, desiredHash); // true = immediate, desiredHash = hash to set
                            } else {
                                // Fallback: update hash immediately if filterController is not available
                                const newUrl = `${window.location.pathname}${window.location.search}${desiredHash}`;
                                window.history.pushState(null, null, newUrl);
                            }
                            
                            // Manually trigger tab switch after preventing default
                            // Bootstrap will handle the tab switch, but hash update is debounced
                            setTimeout(() => {
                                try {
                                    if (window.bootstrap && typeof window.bootstrap.Tab === 'function') {
                                        new window.bootstrap.Tab(tabEl).show();
                                    }
                                } catch (e) {
                                    // Tab switch failed, but hash update is already scheduled
                                }
                            }, 0);
                            
                            return false;
                        }
                    }
                    
                    this.handlers.handleTabClick(event, tabId);
                }, true);
                
                tabEl.addEventListener('shown.bs.tab', (event) => {
                    this.handlers.handleTabShown(event, tabId);
                });
                
                tabEl.addEventListener('hidden.bs.tab', (event) => {
                    this.handlers.handleTabHidden(event, tabId);
                });
            });
            
            if (debugMode === '1') {

            }
            
        } catch (error) {
            if (debugMode === '1') {
            }
            throw error;
        }
    }
    
    /**
     * Bind content tab events
     */
    async bindContentTabEvents() {
        try {
            const contentTabs = document.querySelectorAll('.tab-content .nav-tabs button[data-bs-toggle="tab"]');
            
            contentTabs.forEach((tabEl, index) => {
                const tabId = tabEl.getAttribute('id') || `content-tab-${index}`;
                
                this.state.registerTab(tabId, {
                    element: tabEl,
                    isActive: tabEl.classList.contains('active'),
                    target: tabEl.getAttribute('data-bs-target') || tabEl.getAttribute('href'),
                    type: 'content'
                });
                
                tabEl.addEventListener('click', (event) => {
                    this.handlers.handleContentTabClick(event, tabId);
                });
                
                tabEl.addEventListener('shown.bs.tab', (event) => {
                    this.handlers.handleContentTabShown(event, tabId);
                });
            });
            
            if (debugMode === '1') {

            }
            
        } catch (error) {
            if (debugMode === '1') {
            }
            throw error;
        }
    }
    
    /**
     * Bind navigation events
     */
    async bindNavigationEvents() {
        try {
            window.addEventListener('popstate', (event) => {
                this.navigation.handlePopState(event);
            });
            
            window.addEventListener('hashchange', (event) => {
                this.navigation.handleHashChange(event);
            });
            
            if (debugMode === '1') {

            }
            
        } catch (error) {
            if (debugMode === '1') {
            }
            throw error;
        }
    }
    
    /**
     * Bind keyboard events
     */
    async bindKeyboardEvents() {
        try {
            document.addEventListener('keydown', (event) => {
                this.navigation.handleKeyboardNavigation(event);
            });
            
            if (debugMode === '1') {

            }
            
        } catch (error) {
            if (debugMode === '1') {
            }
            throw error;
        }
    }
    
    /**
     * Load tab content
     */
    loadTabContent(tabId, targetPane) {
        try {
            const paneElement = document.querySelector(targetPane);
            if (!paneElement) return;
            
            if (paneElement.dataset.loaded === 'true') return;
            
            this.triggerTabEvent('tab:load-content', {
                tabId,
                targetPane,
                element: paneElement
            });
            
        } catch (error) {
            if (debugMode === '1') {
            }
        }
    }
    
    /**
     * Load content tab data
     */
    loadContentTabData(tabId, targetPane) {
        try {
            const paneElement = document.querySelector(targetPane);
            if (!paneElement) return;
            
            this.triggerTabEvent('content-tab:load-data', {
                tabId,
                targetPane,
                element: paneElement
            });
            
        } catch (error) {
            if (debugMode === '1') {
            }
        }
    }
    
    /**
     * Trigger custom tab event
     */
    triggerTabEvent(eventName, data) {
        try {
            $(document).trigger(eventName, data);
            this.state.storeEvent(eventName, data);
        } catch (error) {
            if (debugMode === '1') {
            }
        }
    }
    
    /**
     * Update URL hash
     */
    updateUrlHash(hash) {
        this.navigation.updateUrlHash(hash);
    }
    
    /**
     * Activate tab by link
     */
    activateTabByLink(tabLink) {
        return this.navigation.activateTabByLink(tabLink);
    }
    
    /**
     * Add to history
     */
    addToHistory(tabId, tabLink) {
        this.state.addToHistory(tabId, tabLink);
    }
    
    /**
     * Get tab statistics
     */
    getTabStats() {
        return {
            initialized: this.initialized,
            ...this.state.getStats()
        };
    }
}

// Create singleton instance
const tabManager = new TabManager();

// Global exports for backward compatibility
window.tabManager = tabManager;
window.initializeTabEvents = tabManager.initializeTabEvents.bind(tabManager);

// ES Module exports
export { tabManager };
export const { initializeTabEvents } = tabManager;

